import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Filter } from '@loopback/filter';
import { DateTime } from 'luxon';
import { Observable, of, Subject, switchMap, tap } from 'rxjs';
import { ConfigService } from '../config.service';
import { ConsultGl, Flight, FlightDirections, ParticipantSnapshotData, Trip, TripDetail, TripQuickNote } from './commonInterfaces';
import { DataTransform, DateField } from './dataTransformFunctions';
import { CONST_tripUpdated, TripUpdatedArgs } from './trip.service';
import { isDateValidator } from '../ui/validators/isDate.validator';


interface QuickNotePostData {
  note: string
}

export interface RegisteredTeamsSearchParams {
  startDate: Date;
  endDate: Date;
  tripDateRange?: Date[] | null;
  consultantIds?: number[] | null;
}

export interface FlightUpdatedArgs {
  flight?: Flight,
  flightId: number,
  tripId: number,
  action: "add" | "update" | "delete"
}

export interface TripDetailsFilterParams {
  location?: string[];
  community?: string[];
  partner?: string[];
  contact?: string[];
  oldLocation?: string[];
  cost?: string[];
  atltl?: string[];
  atl?: string[];
  tl?: string[];
  recruiter?: string[];
  consultant?: string[];
  triptype?: string[];
  options?: string[];
  office?: string[];
  nameleader?: string;
  startdate?: string;
  enddate?: string;
  tlType?: string[];
  atlType?: string[];
  consultantType?: string[];
  tripDesignations?: string[];
  summaryOnly?: boolean;
  optimize?: boolean;
  limit?: number;
  offset?: number;
  useDatabaseFiltering?: boolean;
}


export interface TripDetailsPartnershipOptionsResponse {
  success: boolean;
  data: TripDetailsParrtnershipOptions;
}
export interface TripDetailsCommunityOption {
  id: string
  name: string
  locId: string
  originalName: string
  locationName: string
  }
  
  export interface TripDetailsPartnerOption {
  id: string
  name: string
  locId: string
  comId: string
  }
  
  export interface TripDetailsContactOption {
  id: string
  name: string
  locId: string
  partnerId: string
  firstname: string
  lastname: string
  }
  
  export interface TripDetailsParrtnershipOptions {
  communities: TripDetailsCommunityOption[]
  partners: TripDetailsPartnerOption[]
  contacts: TripDetailsContactOption[]
  }

export interface TripDetailsResult {
  tripDetails: TripDetailItem[];
  participants: any[];
  summary: {
    totalParticipants: number;
    totalOriginal: number;
    uniqueLocations: number;
    uniquePartners: number;
    uniqueGroups: number;
    totalTrips: number;
  };
}

export interface TripDetailsCommunitiesStructuredResult {
  tripId: number
  id: string
  confirmed: number
  public: number
  name: string
  locId: string
}

export interface TripDetailsCommunitiesStructured {
  tripId: number,
  communities: TripDetailsCommunitiesStructuredResult[]
}

export interface TripDetailsPartnersStructuredResult {
  tripId: number
  id: string
  confirmed: number
  public: number
  name: string
  locId: string
  comId: string
  isPrimary?: number
  isLodging?: number
  isAdditional?: number
}

export interface TripDetailsPartnersStructured {
  tripId: number,
  partners: TripDetailsPartnersStructuredResult[]
}

export interface TripDetailsContactsStructuredResult {
  tripId: number
  id: string
  confirmed: number
  public: number
  firstname: string
  lastname: string
  name: string
  locId: string
  partnerId: string
}

export interface TripDetailsContactsStructured {
  tripId: number,
  contacts: TripDetailsContactsStructuredResult[]
}


export interface TripDetailItem {
  tripId: number
  custId: number | null
  tripDate: Date 
  tripEndDate: Date | null
  active: number | null
  duration: number | null
  driving: number | null
  grouping: string | null
  groupingConfirmed: number | null
  cost: number | null
  attendees: number | null
  attendees_original: number | null
  staffId: number | null
  tl_pub: number | null
  consultId: number | null
  consult_pub: number | null
  salesId: number | null
  airport: string | null
  airportConfirmed: number | null
  accommodations: string | null
  expenses: string | null
  minmoneyest: number | null
  ministrymoney: number | null
  disaster: number | null
  designation: number
  subtrips: number | null
  groupName: string | null
  leader: string | null
  city: string | null
  stateprov: string | null
  tripLeader: string | null
  designationName: string
  designationIcon: string
  rootsLocationName: string | null
  officeName: string | null
  shortcode: string | null
  recruiter: string
  consultant: string
  child: number | null
  serve: number | null
  church: number | null
  sports: number | null
  prayer: number | null
  school: number | null
  mercy: number | null
  evang: number | null
  youth: number | null
  adult: number | null
  med: number | null
  other: number | null
  itineraryId: string | null
  itineraryPublishAccount: number | null
  glName: string | null
  atls: string[]; // Array of ATL names, index corresponds to ATL number
  // Individual ATL fields for edit mode
  atl1?: number | null;
  atl2?: number | null;
  atl3?: number | null;
  atl4?: number | null;
  atl5?: number | null;
  atl6?: number | null;
  atl7?: number | null;
  atl8?: number | null;
  atl9?: number | null;
  atl10?: number | null;
  atl11?: number | null;
  atl12?: number | null;
  atl13?: number | null;
  atl14?: number | null;
  atl15?: number | null;
  atl16?: number | null;
  // Enriched fields from mergeTripData
  groupLeader: string | null;
  communityList: string;
  partnerList: string;
  communityNames: string | null;
  partnerNames: string | null;
  contactNames: string | null;
  contactList: string;
  formResponseCount: number;
  // Check-in and itinerary
  checkedIn: boolean;
  countedParticipants: number;

  flagNames: string | null;

  
  // Additional logistics and contact fields
  meals: string | null;
  lodging: string | null;
  transportation: string | null;
  rec: string | null;
  recLodging: string | null;
  translator: string | null;
  vehicle: string | null;
  // Logistics confirmation fields
  mealsConfirmed: number | null;
  lodgingConfirmed: number | null;
  transportationConfirmed: number | null;
  recreationConfirmed: number | null;
  recLodgingConfirmed: number | null;
  translatorConfirmed: number | null;
  vehicleConfirmed: number | null;
  contact: string | null;
  ministries: string | null;
  estimatedMinistryMoney: number;
  confirmedMinistryMoney: number;
  partners: TripDetailsPartnersStructuredResult[];
  communities: TripDetailsCommunitiesStructuredResult[];
  contacts: TripDetailsContactsStructuredResult[];
}

@Injectable({
  providedIn: 'root'
})
export class TripDetailService {

  constructor(
    private http: HttpClient,
    private config: ConfigService,
  ) {
    this.tripUpdated = CONST_tripUpdated
  }


  tripUpdated: Subject<TripUpdatedArgs>;

  private flightUpdatedSubject: Subject<FlightUpdatedArgs> = new Subject<FlightUpdatedArgs>()
  flightUpdated$ = this.flightUpdatedSubject.asObservable()


  private tripDateFields: DateField<TripDetail>[] = [
    { fieldName: "tripDate", dateType: "local" },
    { fieldName: "dateReserved", dateType: "central" },
    {fieldName: "tripEndDate", dateType: "local"}
  ]

  private flightDateFields: DateField<Flight>[] = [
    { dateType: 'local', fieldName: "flighttodate" },
    { dateType: 'local', fieldName: "flightfromdate" }
  ]

  emptyTrip(): TripDetail {
    return {
      active: 0,
      attendees: 0,
      attendeesOriginal: 0,
      consultantEmail: '',
      consultantId: 0,
      consultantName: "",
      consultingEmails: 0,
      custId: 0,
      dateReserved: new Date(1000, 0, 1),
      domestic: 0,
      dropped: 0,
      duration: 0,
      fmsc: 0,
      glEmail: '',
      glFirstName: '',
      glLastName: '',
      glName: '',
      groupName: '',
      id: 0,
      location: '',
      ministryMoney: 0,
      minMoneyEst: 0,
      ppm: 0,
      recruiterEmail: '',
      recruiterId: 0,
      recruiterName: '',
      showTj: 0,
      subtrips: 0,
      tlEmail: '',
      tlId: 0,
      tlName: '',
      tripDate: new Date(1000, 0, 1),
      tripEndDate: new Date(1000, 0, 1),
      tripYear: '',
      type: 0,
      restricted: 0,
      subtripId: '',
      subtripIds: [],
      hasGroupPhoto: 0,
      designation: 0
    }
  }

  emptyParticipantSnapshotData(): ParticipantSnapshotData {
    return {
      attendees: 0,
      covidAgreeCount: 0,
      covidAgreement: false,
      formsCount: 0,
      legalCount: 0,
      rosterCount: 0,
      shirtCount: 0
    }
  }


  getActiveTripsBetweenDates(startDate: Date, endDate: Date) {
    const filter: Filter<TripDetail> = {
      where: {
        and: [
          { tripDate: { gte: startDate } },
          { tripDate: { lte: endDate } }
        ],
        active: 1
      }
    }

    return this.http.get<TripDetail[]>(`${this.config.dataApiUrl}/trip-details`, { params: { filter: JSON.stringify(filter) } }).pipe(
      switchMap(trips => {
        return DataTransform.replaceDatesArray<TripDetail>(
          trips,
          this.tripDateFields
        )
      })
    )
  }

  getRegisteredTeams(params: RegisteredTeamsSearchParams) {
    const { startDate, endDate, tripDateRange, consultantIds } = params;
    const startDateTime = DateTime.fromJSDate(startDate).setZone("America/Chicago", {keepLocalTime: true}).setZone("utc", {keepLocalTime: true}).toJSDate()
    const endDateTime = DateTime.fromJSDate(endDate).setZone("America/Chicago", {keepLocalTime: true}).setZone("utc", {keepLocalTime: true}).toJSDate()
    const filter: Filter<TripDetail> = {
      where: {
        and: [
          { dateReserved: { gte: startDateTime } },
          { dateReserved: { lte: endDateTime } },
          ...(tripDateRange?.length === 2 && tripDateRange[0] && tripDateRange[1] ? [
            { tripDate: { gte: tripDateRange[0] } },
            { tripDate: { lte: tripDateRange[1] } },
          ] : []),
          ...(consultantIds && consultantIds.length > 0 ? [{ consultantId: { inq: consultantIds } }] : []),
        ],
        dropped: 0,
        custId: { neq: 149},
      }
    }

    return this.http.get<TripDetail[]>(`${this.config.dataApiUrl}/trip-details`, { params: { filter: JSON.stringify(filter) } }).pipe(
      switchMap(trips => {
        return DataTransform.replaceDatesArray<TripDetail>(
          trips,
          this.tripDateFields
        )
      })
    )
  }

  getTripsForCustomer(customerId: number) {
    return this.http.get<TripDetail[]>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/trip-details`).pipe(
      switchMap(trips => {
        return DataTransform.replaceDatesArray<TripDetail>(
          trips,
          this.tripDateFields
        )
      })
    )
  }

  getTripsForCustomerIds(customerIds: number[]) {
    return this.http.get<TripDetail[]>(`${this.config.dataApiUrl}/customers/trip-details/from-list`, {params: {custIds: customerIds}}).pipe(
      switchMap(trips => {
        return DataTransform.replaceDatesArray<TripDetail>(trips, this.tripDateFields)
      })
    )
  }

  getTripById(tripId: number) {
    return this.http.get<TripDetail>(`${this.config.dataApiUrl}/trip-details/${tripId.toString()}`).pipe(
      switchMap(trip => {
        return DataTransform.replaceDates<TripDetail>(
          trip,
          this.tripDateFields
        )
      })
    )
  }

  getTripsByIdList(tripIds: number[]) {

    const params = new HttpParams()
    .set("tripIds", tripIds.join(","))

    return this.http.get<TripDetail[]>(`${this.config.dataApiUrl}/trip-details/by-id-list`, {params: params}).pipe(
      switchMap(trips => {
        return DataTransform.replaceDatesArray(trips, this.tripDateFields)
      })
    )
  }

  getParticipantSnapshot(tripId: number) {
    return this.http.get<ParticipantSnapshotData>(`${this.config.dataApiUrl}/trip-details/${tripId.toString()}/participant-snapshot`)
  }

  getFlights(tripId: number) {
    return this.http.get<Flight[]>(`${this.config.dataApiUrl}/trip-details/${tripId.toString()}/flights`).pipe(
      switchMap(flights => {
        return DataTransform.replaceDatesArray<Flight>(
          flights,
          this.flightDateFields
        )
      })
    )
  }

  addFlight(flightData: Omit<Flight, "id">) {
    return this.http.post<Flight>(`${this.config.dataApiUrl}/trip-details/${flightData.tripId.toString()}/flights`, flightData).pipe(
      switchMap(flight => {
        return DataTransform.replaceDates<Flight>(
          flight,
          this.flightDateFields
        )
      }),
      tap(flight => {
        this.flightUpdatedSubject.next({ flight: flight, flightId: flight.id, tripId: flight.tripId, action: "add" })
      })
    )
  }

  updateFlight(flight: Pick<Flight, "id" | "tripId"> & Partial<Flight>) {
    return this.http.put<Flight>(`${this.config.dataApiUrl}/trip-details/${flight.tripId.toString()}/flights`, flight).pipe(
      switchMap(flight => {
        return DataTransform.replaceDates<Flight>(
          flight,
          this.flightDateFields
        )
      }),
      tap(flight => {
        this.flightUpdatedSubject.next({ flight: flight, flightId: flight.id, tripId: flight.tripId, action: "update" })
      })
    )
  }

  deleteFlight(flight: Pick<Flight, "id" | "tripId"> & Partial<Flight>) {
    return this.http.delete(`${this.config.dataApiUrl}/trip-details/${flight.tripId.toString()}/flights/${flight.id.toString()}`).pipe(

      tap(() => {
        this.flightUpdatedSubject.next({ flightId: flight.id, tripId: flight.tripId, action: "delete" })
      })
    )
  }

  flightForm() {
    return new FormGroup({
      direction: new FormControl<FlightDirections>(FlightDirections.To, { validators: [Validators.required] }),
      airline: new FormControl<string>("", { validators: [Validators.required] }),
      flightNumber: new FormControl<number | null>(null, { validators: [Validators.required, Validators.min(1), Validators.max(9999)] }),
      departureAirport: new FormControl<string>("", { validators: [Validators.required, Validators.minLength(3)] }),
      departureTime: new FormControl<Date | null>(null, { nonNullable: false, validators: [Validators.required, isDateValidator()] }),
      arrivalAirport: new FormControl<string>("", { validators: [Validators.required, Validators.minLength(3)] }),
      arrivalTime: new FormControl<Date | null>(null, { nonNullable: false, validators: [Validators.required, isDateValidator()] }),
      numberOfPeople: new FormControl<number | null>(null, { validators: [Validators.required, Validators.min(1), Validators.max(999)] })

    })
  }

  parseFlightFormTimes(data: { departureTime: Date | null | undefined, arrivalTime: Date | null | undefined }) {
    const departureDate = data.departureTime ? DateTime.fromJSDate(data.departureTime).startOf("day").setZone("utc", { keepLocalTime: true }).toJSDate() : new Date(1000, 0, 1)
    const departureTime = data.departureTime ? DateTime.fromJSDate(data.departureTime).toFormat("HH:mm:ss") : ""
    const arrivalDate = data.arrivalTime ? DateTime.fromJSDate(data.arrivalTime).startOf("day").setZone("utc", { keepLocalTime: true }).toJSDate() : new Date(1000, 0, 1)
    const arrivalTime = data.arrivalTime ? DateTime.fromJSDate(data.arrivalTime).toFormat("HH:mm:ss") : ""

    return {
      departureDate: departureDate,
      departureTime: departureTime,
      arrivalDate: arrivalDate,
      arrivalTime: arrivalTime
    }

  }

  getQuickNote(tripId: number): Observable<string> {
    return this.http.get<TripQuickNote>(`${this.config.dataApiUrl}/trip-details/${tripId.toString()}/quick-note`).pipe(
      switchMap(quickNote => of(quickNote.note))
    )
  }

  updateQuickNote(tripId: number, note: string) {
    const postData: QuickNotePostData = {
      note: note
    }
    return this.http.post(`${this.config.dataApiUrl}/trip-details/${tripId.toString()}/quick-note`, postData)
  }

  getGlConsultingSurvey(tripId: number) {
    return this.http.get<ConsultGl>(`${this.config.dataApiUrl}/trip-details/${tripId.toString()}/gl-consult-survey`)
  }

  getCurrentAndFutureTrips(includeTripIds: number[] = []) {
    const filter: Filter<TripDetail> = {
      where: {
        or: [
          { tripDate: { gte: new Date() } },
          { id: { inq: includeTripIds } }
        ],
        active: 1,
        dropped: 0,
        ppm: 1
      }
    }
    return this.http.get<TripDetail[]>(`${this.config.dataApiUrl}/trip-details/`, { params: { filter: JSON.stringify(filter) } }).pipe(
      switchMap(trips => {
        return DataTransform.replaceDatesArray<TripDetail>(trips, this.tripDateFields)
      })
    )
  }

  getTripsWithItinerary() {
    return this.http.get<TripDetail[]>(`${this.config.dataApiUrl}/trip-details/with-itinerary`).pipe(
      switchMap(trips => {
        return DataTransform.replaceDatesArray<TripDetail>(trips, this.tripDateFields)
      })
    )
  }

  getTripsWithoutItinerary() {
    return this.http.get<TripDetail[]>(`${this.config.dataApiUrl}/trip-details/without-itinerary`).pipe(
      switchMap(trips => {
        return DataTransform.replaceDatesArray<TripDetail>(trips, this.tripDateFields)
      })
    )
  }

  getGroupedTrips(tripId: number) {
    return this.http.get<TripDetail[] >(`${this.config.dataApiUrl}/trip-details/${tripId}/grouped-trips`).pipe(
      switchMap(trips => {
        return DataTransform.replaceDatesArray<TripDetail>(trips, this.tripDateFields)
      })
    )
  }

  getTripDetailsReportData(params: TripDetailsFilterParams): Observable<TripDetailsResult> {
    // Add query optimization parameters
    const optimizedParams = {
      ...params,
      // Add pagination for large datasets
      limit: 1000,
      offset: 0,
      // Add query hints for optimization
      optimize: true,
      // Use database-level filtering instead of application-level
      useDatabaseFiltering: true
    };
    
    return this.http.post<TripDetailsResult>(`${this.config.dataApiUrl}/trip-details/report`, optimizedParams).pipe(
      switchMap(result => {
         DataTransform.replaceDatesArrayDirect<TripDetailItem>(result.tripDetails, [{fieldName: "tripDate", dateType: "local"}, {fieldName: "tripEndDate", dateType: "local"}]);

        return of (result)
      })
    );
  }

  // New method for paginated results
  getTripDetailsReportDataPaginated(params: TripDetailsFilterParams, page: number = 1, pageSize: number = 100): Observable<TripDetailsResult> {
    const paginatedParams = {
      ...params,
      limit: pageSize,
      offset: (page - 1) * pageSize,
      optimize: true,
      useDatabaseFiltering: true
    };
    
    return this.http.post<TripDetailsResult>(`${this.config.dataApiUrl}/trip-details/report`, paginatedParams);
  }

  // New method for lightweight summary data
  getTripDetailsSummary(params: TripDetailsFilterParams): Observable<{
    totalTrips: number;
    totalAttendees: number;
    uniqueLocations: number;
    uniqueGroups: number;
    estimatedDataSize: number;
  }> {
    const summaryParams = {
      ...params,
      summaryOnly: true,
      optimize: true
    };
    
    return this.http.post<{
      totalTrips: number;
      totalAttendees: number;
      uniqueLocations: number;
      uniqueGroups: number;
      estimatedDataSize: number;
    }>(`${this.config.dataApiUrl}/trip-details/summary`, summaryParams);
  }

  exportTripDetailsCSV(params: TripDetailsFilterParams): Observable<Blob> {
    return this.http.post(`${this.config.dataApiUrl}/trip-details/csv`, params, { responseType: 'blob' });
  }

  updateTripField(tripId: number, field: string, value: any): Observable<any> {
    return this.http.put(`${this.config.dataApiUrl}/trip-details/${tripId}`, { [field]: value });
  }

  getPartnershipOptions(tripId: number): Observable<any> {
    const url = `${this.config.dataApiUrl}/trip-details/partnership-options/${tripId}`;
    return this.http.get(url);
  }

  getAllPartnershipOptions(): Observable<TripDetailsPartnershipOptionsResponse> {
    const url = `${this.config.dataApiUrl}/trip-details/all-partnership-options`;
    return this.http.get<TripDetailsPartnershipOptionsResponse>(url);
  }

  getOfficeOptions(): Observable<any> {
    const url = `${this.config.dataApiUrl}/trip-details/office-options`;
    return this.http.get(url);
  }

  getTripTypeOptions(): Observable<any> {
    const url = `${this.config.dataApiUrl}/trip-details/trip-type-options`;
    return this.http.get(url);
  }

  getCostOptions(): Observable<any> {
    const url = `${this.config.dataApiUrl}/trip-details/cost-options`;
    return this.http.get(url);
  }

  getTripDesignationOptions(): Observable<any> {
    const url = `${this.config.dataApiUrl}/trip-details/trip-designation-options`;
    return this.http.get(url);
  }
}
